import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  QrCode, Package, CheckCircle, Truck, LogOut,
  Camera, CameraOff, Scan, Loader2, MapPin,
  Thermometer, Battery, Clock, ChevronRight, Hash
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Shipper Login ──────────────────────────────────────

const ShipperLogin = ({ onLogin }) => {
  const [mode, setMode] = useState('email'); // email | pin
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/shipper/login/pin`, { pin });
      onLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="shipper-login">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-3">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-xl">SmartPallet Shipper</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Scan pallets and activate shipments</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition ${mode === 'email' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
              onClick={() => setMode('email')}
              data-testid="email-login-tab"
            >
              Email
            </button>
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition ${mode === 'pin' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
              onClick={() => setMode('pin')}
              data-testid="pin-login-tab"
            >
              PIN Code
            </button>
          </div>

          {mode === 'email' ? (
            <form onSubmit={handleEmailLogin} className="space-y-3">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="shipper-email-input" placeholder="name@company.com" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="shipper-password-input" />
              </div>
              <Button className="w-full" type="submit" disabled={loading} data-testid="shipper-login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sign In
              </Button>
            </form>
          ) : (
            <form onSubmit={handlePinLogin} className="space-y-3">
              <div>
                <Label htmlFor="pin">PIN Code</Label>
                <Input
                  id="pin" type="tel" inputMode="numeric" pattern="[0-9]*"
                  value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required data-testid="shipper-pin-input"
                  placeholder="Enter your PIN"
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                />
              </div>
              <Button className="w-full" type="submit" disabled={loading} data-testid="shipper-pin-login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Login with PIN
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Scanner Component ──────────────────────────────────

const PalletScanner = ({ onScan, onClose }) => {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [hasCamera, setHasCamera] = useState(true);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    let html5QrCode = null;

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        html5QrCode = new Html5Qrcode('shipper-scanner');
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 280, height: 150 }, aspectRatio: 1.5 },
          (text) => {
            html5QrCode.stop().catch(() => {});
            onScan(text);
          },
          () => {}
        );
      } catch {
        setHasCamera(false);
      }
    };

    startScanner();

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, [onScan]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) onScan(manualCode.trim());
  };

  return (
    <div className="space-y-4" data-testid="pallet-scanner">
      {hasCamera ? (
        <div className="relative">
          <div id="shipper-scanner" className="w-full rounded-xl overflow-hidden bg-black" style={{ minHeight: 250 }} />
          <p className="text-center text-sm text-slate-500 mt-2">Point camera at pallet barcode or QR code</p>
        </div>
      ) : (
        <div className="text-center py-8 bg-slate-100 rounded-xl">
          <CameraOff className="w-10 h-10 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Camera not available</p>
        </div>
      )}

      {/* Manual entry — always visible */}
      <div className="border-t pt-4">
        <p className="text-xs text-slate-500 mb-2 text-center">Or enter pallet code manually</p>
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <Input
            value={manualCode}
            onChange={e => setManualCode(e.target.value.toUpperCase())}
            placeholder="5-char code or IMEI"
            className="text-center font-mono text-lg tracking-wider"
            data-testid="manual-pallet-input"
            maxLength={20}
          />
          <Button type="submit" disabled={!manualCode.trim()} data-testid="manual-pallet-submit">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};

// ── Main Shipper App ───────────────────────────────────

const ShipperApp = () => {
  const navigate = useNavigate();
  const { user, token, logout, loginWithToken } = useAuth();
  const [view, setView] = useState('home'); // home | scan | activate | shipments
  const [scannedCode, setScannedCode] = useState('');
  const [palletInfo, setPalletInfo] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [palletCount, setPalletCount] = useState('');
  const [activating, setActivating] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);

  // If not logged in as shipper, show login
  const isShipper = user && ['shipper', 'owner', 'superadmin'].includes(user.role);

  const handleLogin = (data) => {
    loginWithToken(data.access_token, data.user);
  };

  // Fetch shipments
  const fetchShipments = useCallback(async () => {
    setLoadingShipments(true);
    try {
      const res = await axios.get(`${API}/shipper/my-shipments`);
      setShipments(res.data);
    } catch {
      // Fallback to regular shipments
      try {
        const res = await axios.get(`${API}/shipments`);
        setShipments(res.data);
      } catch { /* ignore */ }
    } finally {
      setLoadingShipments(false);
    }
  }, []);

  useEffect(() => {
    if (isShipper) fetchShipments();
  }, [isShipper, fetchShipments]);

  // Handle scan result
  const handleScan = async (code) => {
    setScannedCode(code);
    try {
      const res = await axios.get(`${API}/shipper/lookup/${encodeURIComponent(code)}`);
      setPalletInfo(res.data);
      setView('activate');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`Pallet not found: ${code}`);
      } else {
        toast.error('Lookup failed');
      }
      // Still go to activate view with manual entry
      setPalletInfo(null);
      setView('activate');
    }
  };

  // Activate shipment
  const handleActivate = async () => {
    if (!orderNumber.trim()) {
      toast.error('Order number (Mottakernummer) is required');
      return;
    }
    setActivating(true);
    try {
      const res = await axios.post(`${API}/shipper/activate`, {
        pallet_code: scannedCode,
        order_number: orderNumber.trim(),
        pallet_count: palletCount ? parseInt(palletCount) : null,
      });
      toast.success(res.data.message);
      setView('home');
      setScannedCode('');
      setOrderNumber('');
      setPalletCount('');
      setPalletInfo(null);
      fetchShipments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/shipper');
  };

  if (!isShipper) {
    return <ShipperLogin onLogin={handleLogin} />;
  }

  // ── HOME VIEW ────────────────────────────────────────
  if (view === 'home') {
    const activeShipments = shipments.filter(s => ['activated', 'in_transit'].includes(s.status));
    const recentShipments = shipments.filter(s => !['activated', 'in_transit'].includes(s.status)).slice(0, 5);

    return (
      <div className="min-h-screen bg-slate-50" data-testid="shipper-home">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-slate-900">SmartPallet</h1>
              <p className="text-xs text-slate-500">{user.name} {user.gln ? `(GLN: ${user.gln})` : ''}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="shipper-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
          {/* Main Action */}
          <Button
            className="w-full h-20 text-lg bg-blue-600 hover:bg-blue-700"
            onClick={() => setView('scan')}
            data-testid="scan-pallet-btn"
          >
            <Scan className="w-6 h-6 mr-3" />
            Scan Pallet
          </Button>

          {/* Active Shipments */}
          {activeShipments.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 mb-2">Active Shipments ({activeShipments.length})</h2>
              <div className="space-y-2">
                {activeShipments.map(s => (
                  <ShipmentCard key={s.id} shipment={s} />
                ))}
              </div>
            </div>
          )}

          {/* Recent */}
          {recentShipments.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 mb-2">Recent</h2>
              <div className="space-y-2">
                {recentShipments.map(s => (
                  <ShipmentCard key={s.id} shipment={s} compact />
                ))}
              </div>
            </div>
          )}

          {shipments.length === 0 && !loadingShipments && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No shipments yet</p>
              <p className="text-sm text-slate-400">Scan a pallet to create your first shipment</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── SCAN VIEW ────────────────────────────────────────
  if (view === 'scan') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="shipper-scan-view">
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center">
            <Button variant="ghost" size="sm" onClick={() => setView('home')} data-testid="back-to-home">
              <ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Back
            </Button>
            <h1 className="text-lg font-bold text-slate-900 ml-2">Scan Pallet</h1>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-6">
          <PalletScanner onScan={handleScan} onClose={() => setView('home')} />
        </div>
      </div>
    );
  }

  // ── ACTIVATE VIEW ────────────────────────────────────
  if (view === 'activate') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="shipper-activate-view">
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center">
            <Button variant="ghost" size="sm" onClick={() => setView('scan')} data-testid="back-to-scan">
              <ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Back
            </Button>
            <h1 className="text-lg font-bold text-slate-900 ml-2">Activate Shipment</h1>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
          {/* Pallet Info */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-mono font-bold text-slate-900" data-testid="scanned-code">{scannedCode}</p>
                  <p className="text-xs text-slate-500">
                    {palletInfo ? `${palletInfo.name} - ${palletInfo.status}` : 'Pallet code'}
                  </p>
                </div>
                {palletInfo && !palletInfo.has_active_shipment && (
                  <Badge className="ml-auto bg-green-100 text-green-700">Available</Badge>
                )}
                {palletInfo?.has_active_shipment && (
                  <Badge className="ml-auto bg-red-100 text-red-700">In Use</Badge>
                )}
              </div>
              {palletInfo && (
                <div className="grid grid-cols-3 gap-2 text-xs text-center">
                  {palletInfo.last_temperature != null && (
                    <div className="bg-slate-50 rounded p-2">
                      <Thermometer className="w-3 h-3 mx-auto mb-1 text-blue-500" />
                      {palletInfo.last_temperature.toFixed(1)}°C
                    </div>
                  )}
                  {palletInfo.battery_level != null && (
                    <div className="bg-slate-50 rounded p-2">
                      <Battery className="w-3 h-3 mx-auto mb-1 text-green-500" />
                      {Math.round(palletInfo.battery_level)}%
                    </div>
                  )}
                  {palletInfo.last_seen && (
                    <div className="bg-slate-50 rounded p-2">
                      <Clock className="w-3 h-3 mx-auto mb-1 text-slate-400" />
                      {new Date(palletInfo.last_seen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Number (Required) */}
          <div className="space-y-2">
            <Label htmlFor="order_number" className="text-base font-semibold">
              Mottakernummer / Order Number *
            </Label>
            <Input
              id="order_number"
              value={orderNumber}
              onChange={e => setOrderNumber(e.target.value)}
              placeholder="Enter order number"
              className="text-lg h-12"
              data-testid="order-number-input"
              required
              autoFocus
            />
          </div>

          {/* Pallet Count (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="pallet_count" className="text-base">
              Number of Pallets <span className="text-slate-400 text-sm">(optional)</span>
            </Label>
            <Input
              id="pallet_count"
              type="number"
              inputMode="numeric"
              value={palletCount}
              onChange={e => setPalletCount(e.target.value)}
              placeholder="e.g. 12"
              className="text-lg h-12"
              data-testid="pallet-count-input"
              min={1}
            />
          </div>

          {/* Activate Button */}
          <Button
            className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
            onClick={handleActivate}
            disabled={activating || !orderNumber.trim() || (palletInfo?.has_active_shipment)}
            data-testid="activate-shipment-btn"
          >
            {activating ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <CheckCircle className="w-5 h-5 mr-2" />
            )}
            {activating ? 'Activating...' : 'Activate Shipment'}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

// ── Shipment Card Component ────────────────────────────

const ShipmentCard = ({ shipment, compact = false }) => {
  const statusColors = {
    activated: 'bg-blue-100 text-blue-700',
    in_transit: 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-50 text-red-600',
    geofence_entered: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <Card className={compact ? 'opacity-70' : ''} data-testid={`shipment-card-${shipment.id}`}>
      <CardContent className={compact ? 'py-3 px-4' : 'pt-4'}>
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {shipment.order_number && (
                <span className="font-bold text-slate-900 text-sm" data-testid="shipment-order">
                  <Hash className="w-3 h-3 inline mr-0.5" />{shipment.order_number}
                </span>
              )}
              <Badge className={`text-xs ${statusColors[shipment.status] || 'bg-slate-100 text-slate-600'}`}>
                {shipment.status?.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex gap-3 mt-1 text-xs text-slate-500">
              {shipment.origin && <span>{shipment.origin}</span>}
              {shipment.origin && shipment.destination && <span>→</span>}
              {shipment.destination && <span>{shipment.destination}</span>}
            </div>
            {!compact && (
              <div className="flex gap-3 mt-2 text-xs text-slate-400">
                {shipment.pallet_count && <span>{shipment.pallet_count} pallets</span>}
                {shipment.receiver_name && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {shipment.receiver_name}
                  </span>
                )}
                {shipment.receiver_gln && <span>GLN: {shipment.receiver_gln}</span>}
              </div>
            )}
          </div>
          {!compact && shipment.owner_api_confirmed && (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ShipperApp;
